import { buildQueryString } from '../utils/buildQueryString'
import { createRestApiClient } from '../utils/createRestApiClient'
import { sign } from '../utils/sign'

const BASE_URL = 'https://api.bitget.com'

const createAuthHeaders = async (params: {
  /** 認証用 API Key */
  apiKey: string
  /** 認証用 API Secret */
  apiSecret: string
  /** 認証用 API Passphrase */
  apiPassphrase: string
  /** request headers 作成関数 (関数内で作成する headers を上書きして強制適用させる header) */
  createHeaders?: () => Promise<Record<string, string>>
  dataString: string
  method: 'GET' | 'POST'
  path: string
}): Promise<Record<string, string>> => {
  const {
    apiKey,
    apiSecret,
    apiPassphrase,
    createHeaders,
    dataString,
    method,
    path,
  } = params

  // 署名対象文字列の作成
  const timestamp = Date.now()
  const signTarget = `${timestamp}${method}${path}${dataString}`

  // HMAC-SHA256署名の生成とbase64エンコード
  const signature = await sign({
    algorithm: 'sha256',
    payload: signTarget,
    secret: apiSecret,
    encoding: 'base64',
  })

  // 強制適用する request headers 作成
  const overrideHeaders = createHeaders ? await createHeaders() : {}

  return {
    'Content-Type': 'application/json',
    locale: 'en-US',
    'ACCESS-KEY': apiKey,
    'ACCESS-SIGN': signature,
    'ACCESS-TIMESTAMP': timestamp.toString(),
    'ACCESS-PASSPHRASE': apiPassphrase,
    ...overrideHeaders,
  }
}

export const createClient = ({
  apiKey,
  apiSecret,
  apiPassphrase,
  createHeaders,
  proxy,
}: {
  /** 認証用 API Key */
  apiKey: string
  /** 認証用 API Secret */
  apiSecret: string
  /** 認証用 API Passphrase */
  apiPassphrase: string
  /** request headers 作成関数 (関数内で作成する headers を上書きして強制適用させる header) */
  createHeaders?: () => Promise<Record<string, string>>
  /** request を proxy する場合の proxy base URL */
  proxy?: string
}) =>
  createRestApiClient({
    buildRequestData: async ({ method, path, query, body }) => {
      const queryString = buildQueryString(query)
      const queryWithQuestionMark = queryString ? `?${queryString}` : ''
      const bodyString = body ? JSON.stringify(body) : ''
      const url = `${BASE_URL}${path}${queryWithQuestionMark}`
      const dataString = queryWithQuestionMark + bodyString
      const headers = await createAuthHeaders({
        apiKey,
        apiSecret,
        apiPassphrase,
        createHeaders,
        dataString,
        method,
        path,
      })
      return {
        url,
        headers,
        body: method === 'POST' ? bodyString : undefined,
      }
    },
    getDelayForRetry: ({ statusCode }) => {
      // 401 エラーは 1秒待機してリトライ
      if (statusCode === 401) {
        return 1000
      }
      // 429 エラーは 10秒待機してリトライ
      if (statusCode === 429) {
        return 10_000
      }
      // 5xx エラーは 1秒待機してリトライ
      if (statusCode >= 500) {
        return 1000
      }
      // それ以外のエラーはリトライしない
      return undefined
    },
    proxy,
  })
